import { AnalyticsEventName } from '@schooly/analytics';
import { trackAmplitudeEvent } from '@schooly/analytics';
import { UPDATE_THREAD_MUTATION } from '@schooly/api';
import {
  ChannelDateSeparator,
  ChannelInput,
  ChannelInputRef,
  ChannelLoading,
  Message,
  MessageSystem,
  NoChannelSelectedStub,
  NoMessagesStub,
  useThreads,
} from '@schooly/components/threads';
import { SxProps, Theme } from '@mui/material';
import { ModalContent, ModalMain } from '@schooly/style';
import { useIsMutating } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useRef } from 'react';
import { Channel } from 'stream-chat-react';

import { AccessDenied } from '@schooly/style';
import { MessageChannel } from './MessageChannelContent';

type MessageModalPreviewSelectedThreadProps = {
  isThreadFetching: boolean;
  hideChannelPreview?: boolean;
  contentSx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  EmptyStateIndicator?: React.ComponentType;
  ChannelLoadingComponent?: React.ComponentType;
};

const defaultContentSx: SxProps<Theme> = {
  px: 0,
  pt: 0,
  backgroundColor: 'common.lightBg',
  '.str-chat__virtual-list, .str-chat__container, .str-chat__channel': {
    backgroundColor: 'common.lightBg',
  },
};

export const MessageModalPreviewSelectedThread: FC<MessageModalPreviewSelectedThreadProps> = ({
  isThreadFetching,
  hideChannelPreview,
  contentSx,
  inputSx,
  EmptyStateIndicator = NoMessagesStub,
  ChannelLoadingComponent = ChannelLoading,
}) => {
  const channelInputRef = useRef<ChannelInputRef>(null);

  const {
    connectedUser,
    isUserConnecting,
    isChannelCreating,
    isThreadMembersUpdating,
    selectedThread,
    streamChannel,
    isChannelLoading,
    client,
  } = useThreads();

  const isThreadUpdating = useIsMutating({
    mutationKey: [UPDATE_THREAD_MUTATION],
  });

  const isLoading =
    isUserConnecting ||
    isChannelCreating ||
    isThreadMembersUpdating ||
    isThreadFetching ||
    isChannelLoading ||
    isThreadUpdating;

  const hasAccessToChannel = selectedThread?.channel?.members.some(
    (s) => s.user.user_id === connectedUser?.user_id,
  );

  const handleSubmit = useCallback(
    (_: React.KeyboardEvent<HTMLInputElement>, text: string) => {
      if (!connectedUser) return;

      trackAmplitudeEvent({
        name: AnalyticsEventName.SendThreadMessage,
        payload: { message_length: text.length, user_id: connectedUser.user_id },
      });
    },
    [connectedUser],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    if (streamChannel && selectedThread) {
      const subscription = streamChannel.on('notification.mark_read', async (e) => {
        trackAmplitudeEvent({
          name: AnalyticsEventName.ClearMessageNotifications,
          payload: {
            message_id: selectedThread.root_entity_id,
            cleared_at: new Date().toISOString(),
          },
        });
      });

      return subscription.unsubscribe;
    }
  }, [streamChannel, selectedThread, client?.state]);

  const renderContent = () => {
    if (!selectedThread && !streamChannel && !isLoading && !connectedUser) {
      return <NoChannelSelectedStub />;
    }

    if (isLoading || !connectedUser) return <ChannelLoadingComponent />;
    if (!hasAccessToChannel) return <AccessDenied />;

    return (
      <Channel
        Message={Message}
        Input={() => <ChannelInput ref={channelInputRef} onSubmit={handleSubmit} sx={inputSx} />}
        EmptyStateIndicator={EmptyStateIndicator}
        DateSeparator={ChannelDateSeparator}
        UnreadMessagesSeparator={() => null}
        LoadingIndicator={ChannelLoadingComponent}
        UnreadMessagesNotification={() => null}
        channel={streamChannel}
        MessageNotification={() => null}
        MessageSystem={MessageSystem}
      >
        <MessageChannel
          connectedUser={connectedUser}
          onMessageEdit={(m) => channelInputRef.current?.onMessageEdit(m)}
          hideChannelPreview={hideChannelPreview}
        />
      </Channel>
    );
  };

  return (
    <ModalMain>
      <ModalContent
        sx={
          contentSx
            ? [defaultContentSx, ...(Array.isArray(contentSx) ? contentSx : [contentSx])]
            : defaultContentSx
        }
      >
        {renderContent()}
      </ModalContent>
    </ModalMain>
  );
};
